import { timestampDate } from '@bufbuild/protobuf/wkt'
import {
  ColDef,
  ICellRendererParams,
  IRowNode,
  ISetFilterParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community'

import { Alert, AlertStatus } from '@/gen/inventory/v1/alert_pb'
import { RiskLevel } from '@/gen/inventory/v1/risk_pb'
import type { SignalType } from '@/gen/inventory/v1/signal_pb'

import { alertStatusLabel, riskLevelLabel } from '@/const/label'
import { alertStatusPriority, riskLevelPriority } from '@/const/priority'

import { CompanyDrawerSeverityCell } from '@/pages/company-drawer/ag-alert-table/components/cells/severity-cell'
import { AlertStatusCell } from '@/pages/company-drawer/ag-alert-table/components/cells/status-cell'

import { DateCell } from '@/components/ag-cells/date-cell/date-cell'
import { alertNameColumnDef } from '@/components/shared/signal-type/alert-name-column'

import { WIDTH_ALERT, WIDTH_DATE_DETECTED, WIDTH_SEVERITY, WIDTH_STATUS } from './consts'
import { ALERT_COLUMN_LABEL } from './labels'

export interface AlertColumnDefsConfig {
  rows: readonly Alert[]
  allScopeValues: string[]
  disableDefaultFilter?: boolean
  readOnly?: boolean
  titleByType?: ReadonlyMap<SignalType, string>
}

export const getCompanyDrawerAlertColumnDefs = ({
  rows,
  allScopeValues,
  disableDefaultFilter,
  readOnly = false,
  titleByType = new Map(),
}: AlertColumnDefsConfig): ColDef<Alert>[] => {
  const cols: ColDef<Alert>[] = [
    // Hidden scope column for filtering (no longer on Alert; kept for structural compatibility)
    {
      colId: 'scope',
      headerName: 'Scope',
      hide: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        suppressMiniFilter: true,
        selectAllOnMiniFilter: true,
        suppressSyncValuesAfterDataChange: true,
        values: allScopeValues,
      } as ISetFilterParams,
      valueGetter: () => '',
    },
    {
      colId: 'severity',
      headerName: 'Severity',
      flex: 1,
      minWidth: WIDTH_SEVERITY,
      cellRenderer: CompanyDrawerSeverityCell,
      sortable: true,
      filter: 'agSetColumnFilter',
      sort: 'asc', // Default sort order
      filterParams: {
        values: Object.values(RiskLevel)
          .filter((v) => typeof v === 'number' && v !== RiskLevel.UNSPECIFIED)
          .map((v) => riskLevelLabel[v as RiskLevel]),
        suppressMiniFilter: true,
        selectAllOnMiniFilter: true,
        suppressSyncValuesAfterDataChange: true,
      } as ISetFilterParams,
      comparator: (_valueA, _valueB, nodeA, nodeB) => {
        const severityA = nodeA?.data?.severity ?? RiskLevel.UNSPECIFIED
        const severityB = nodeB?.data?.severity ?? RiskLevel.UNSPECIFIED
        return riskLevelPriority[severityA] - riskLevelPriority[severityB]
      },
      valueGetter: (params: ValueGetterParams<Alert>) => {
        const severity = params.data?.severity
        return severity ? riskLevelLabel[severity] || '' : ''
      },
      valueFormatter: (params: ValueFormatterParams<Alert, string>) => params.value || '',
    },
    alertNameColumnDef<Alert>({
      colId: 'alert',
      headerName: ALERT_COLUMN_LABEL,
      filterName: ALERT_COLUMN_LABEL,
      rows,
      titleByType,
      getSignal: (row) => row?.signal,
      flex: 3,
      minWidth: WIDTH_ALERT,
    }),
    {
      colId: 'dateDetected',
      headerName: 'Date detected',
      flex: 1,
      minWidth: WIDTH_DATE_DETECTED,
      cellRenderer: (params: ICellRendererParams<Alert>) => <DateCell data={params.data?.signal} />,
      sortable: true,
      filter: 'agDateColumnFilter',
      comparator: (
        _valueA: unknown,
        _valueB: unknown,
        nodeA: IRowNode<Alert>,
        nodeB: IRowNode<Alert>,
      ) => {
        const dateA = nodeA.data?.createdAt ? timestampDate(nodeA.data.createdAt) : null
        const dateB = nodeB.data?.createdAt ? timestampDate(nodeB.data.createdAt) : null
        if (!dateA && !dateB) return 0
        if (!dateA) return 1
        if (!dateB) return -1
        return dateA.getTime() - dateB.getTime()
      },
      valueGetter: (params: ValueGetterParams<Alert>) => params.data?.createdAt,
    },
    {
      colId: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: WIDTH_STATUS,
      cellRenderer: AlertStatusCell,
      cellRendererParams: { readOnly },
      sortable: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: Object.values(AlertStatus)
          .filter((v) => typeof v === 'number' && v !== AlertStatus.UNSPECIFIED)
          .map((v) => alertStatusLabel[v]),
        suppressMiniFilter: true,
        selectAllOnMiniFilter: true,
        suppressSyncValuesAfterDataChange: true,
        // Set default selected filters if not disabled
        defaultToNothingSelected: disableDefaultFilter,
        defaultValue: disableDefaultFilter
          ? undefined
          : [alertStatusLabel[AlertStatus.OPEN], alertStatusLabel[AlertStatus.ONGOING]],
      } as ISetFilterParams,
      comparator: (_valueA, _valueB, nodeA, nodeB) => {
        const statusA = nodeA?.data?.workStatus ?? AlertStatus.UNSPECIFIED
        const statusB = nodeB?.data?.workStatus ?? AlertStatus.UNSPECIFIED
        return alertStatusPriority[statusA] - alertStatusPriority[statusB]
      },
      valueGetter: (params: ValueGetterParams<Alert>) => {
        const status = params.data?.workStatus
        return status ? alertStatusLabel[status] || '' : ''
      },
      valueFormatter: (params: ValueFormatterParams<Alert, string>) => params.value || '',
    },
  ]

  return cols
}
